import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ArrowRight, CalendarBlank, HandCoins, SealCheck } from "@phosphor-icons/react";
import { Reveal, SectionHead } from "./Reveal";
import { PROPERTIES } from "@/data/content";
import { useLanguage } from "@/i18n";

const DEVS = ["all", "sobha", "damac", "binghatti", "omniyat"];

export default function Properties() {
  const { lang, t } = useLanguage();
  const [filter, setFilter] = useState("all");
  const list = filter === "all" ? PROPERTIES : PROPERTIES.filter((p) => p.dev === filter);

  return (
    <section id="properties" data-testid="properties-section" className="bg-[#0D0D0D] py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <SectionHead index={t.properties.index} overline={t.properties.overline} title={t.properties.title} testid="properties" />

        <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-3">
          {DEVS.map((d) => (
            <button
              key={d}
              data-testid={`filter-${d}`}
              onClick={() => setFilter(d)}
              className={`border px-5 py-2 text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                filter === d ? "border-gold bg-gold text-charcoal" : "border-gold/30 text-gold hover:bg-gold/10"
              }`}
            >
              {t.properties.filters[d]}
            </button>
          ))}
        </Reveal>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {list.map((p) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, ease: [0.33, 1, 0.68, 1] }}
                data-testid={`property-card-${p.id}`}
                className="group flex h-full flex-col border border-gold/15 bg-surface transition-colors duration-500 hover:border-gold/60"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
                  <span className="absolute left-4 top-4 border border-gold/40 bg-charcoal/80 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-gold backdrop-blur-sm rtl:left-auto rtl:right-4">
                    {t.properties.filters[p.dev]}
                  </span>
                  <span className="absolute right-4 top-4 bg-gold px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-charcoal rtl:left-4 rtl:right-auto">
                    {t.properties.offplan}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-serif text-xl tracking-wide text-[#FAFAFA]">{p.name}</h3>
                  <p className="mt-2 flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#A3A3A3]">
                    <MapPin size={14} className="shrink-0 text-gold" /> {p.location[lang]}
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]/80">{p.type[lang]}</p>
                  <p className="mt-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]/80">
                    <CalendarBlank size={13} className="shrink-0 text-gold/70" /> {t.properties.handover}: {p.completion[lang]}
                  </p>
                  <p className="mt-3 flex-1 text-xs leading-relaxed text-[#A3A3A3]">{p.features[lang]}</p>
                  <p className="mt-4 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-[#A3A3A3]/80">
                    <HandCoins size={13} className="shrink-0 text-gold/70" /> {t.properties.payment}: {p.payment[lang]}
                  </p>
                  {p.visa && (
                    <span className="mt-3 inline-flex w-fit items-center gap-1.5 border border-gold/30 px-2.5 py-1 text-[9px] uppercase tracking-[0.2em] text-gold">
                      <SealCheck size={12} weight="duotone" /> {t.properties.visa}
                    </span>
                  )}
                  <div className="mt-5 flex items-center justify-between border-t border-gold/10 pt-4">
                    <span className="font-serif text-lg text-gold-gradient">{p.price[lang]}</span>
                    <Link
                      data-testid={`property-details-${p.id}`}
                      to={`/project/${p.id}`}
                      className="flex items-center gap-2 border border-gold px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold/10"
                    >
                      {t.properties.viewDetails} <ArrowRight size={12} className="rtl:rotate-180" />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
